import React, {useContext, useRef} from 'react';
import PropTypes from 'prop-types';
import NavigationBar from '../../../shared/library/navigation-bar';
import {FOCUS_BLUE, GUNSMOKE} from '../../../shared/style/colors';
import RepositoriesIcon from '../../../shared/library/icons/nav/repositories.svg';
import DecisionsIcon from '../../../shared/library/icons/nav/decisions.svg';
import BlogsIcon from '../../../shared/library/icons/nav/blog.svg';
import TechTalksIcom from '../../../shared/library/icons/nav/techtalks.svg';
import Jobs from '../../../shared/library/icons/nav/jobs.svg';
import StacksIcon from '../../../shared/library/icons/nav/stack.svg';
import FollowersIcon from '../../../shared/library/icons/nav/followers.svg';
import TeamIcon from '../../../shared/library/icons/nav/team.svg';
import MembersIcon from '../../../shared/library/icons/nav/members.svg';
import ApplicationIcon from '../../../shared/library/icons/dropdown-icon-apps.svg';
import glamorous from 'glamorous';
import {useSendAnalyticsEvent} from '../../../shared/enhancers/analytics-enhancer';
import {PrivateModeContext} from '../../../shared/enhancers/private-mode-enchancer';
import {isPrivateMember} from '../../../shared/utils/is-private-member';
import {STACK_APPLICATIONS} from '../../../shared/constants/features';
import {featurePermissionCheck} from '../../../shared/utils/feature-permission-check';
import {NAVBAR_HEIGHT} from '../../../shared/style/dimensions';
import {
  ORG_STRUCTURE_MODES,
  OrganizationContext
} from '../../../shared/enhancers/organization-enhancer';

const defaultActiveStyles = {
  color: FOCUS_BLUE,
  borderColor: FOCUS_BLUE
};

const activeStyles = {
  ...defaultActiveStyles,
  ' svg path': {
    fill: FOCUS_BLUE,
    stroke: FOCUS_BLUE
  }
};

const Container = glamorous.div({
  gridArea: 'nav',
  position: 'sticky',
  top: NAVBAR_HEIGHT,
  zIndex: 2,
  boxShadow: '0 1px #ebebeb'
});

const JobsIcon = glamorous(Jobs)({
  width: 20,
  height: 20,
  ' path': {
    fill: GUNSMOKE
  }
});

const Navigation = ({company, children}) => {
  const stickyRef = useRef(null);
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const isPrivateMode = useContext(PrivateModeContext);
  const {orgStructureMode} = useContext(OrganizationContext);
  const isPrivate = isPrivateMember(company.id, isPrivateMode);
  const stackApplicationsPermission = featurePermissionCheck(isPrivateMode, STACK_APPLICATIONS);

  const allMenuOptions = [
    {
      name: 'Jobs',
      icon: <JobsIcon />,
      slug: 'jobs',
      activeStyles,
      visible: company.jobs.count > 0
    },
    {
      name: 'Decisions',
      icon: <DecisionsIcon />,
      slug: 'decisions',
      activeStyles,
      visible: company.stackDecisions.count > 0
    },
    {
      name: 'Tech Stacks',
      icon: <StacksIcon />,
      slug: 'tech-stacks',
      activeStyles,
      visible: company.stacksList.count > 0
    },
    {
      name: 'Applications',
      icon: <ApplicationIcon />,
      slug: 'applications',
      activeStyles: {
        ...defaultActiveStyles,
        ' svg g g': {
          fill: FOCUS_BLUE
        }
      },
      visible:
        isPrivateMode &&
        !isPrivateMode.loading &&
        company.privateMode &&
        isPrivate &&
        company.stackApps.count > 0 &&
        stackApplicationsPermission
    },
    {
      name: 'Teams',
      icon: <TeamIcon />,
      slug: 'teams',
      activeStyles,
      visible:
        isPrivateMode &&
        company.privateTeams.count > 0 &&
        orgStructureMode !== ORG_STRUCTURE_MODES.flexible
    },
    {
      name: 'Blog Posts',
      icon: <BlogsIcon />,
      slug: 'blog-posts',
      activeStyles,
      visible: company.blogPosts.count > 0
    },
    {
      name: 'Open Source ',
      icon: <RepositoriesIcon />,
      slug: 'open-source',
      activeStyles,
      visible: company.githubRepos.count > 0
    },
    {
      name: 'Tech Talks',
      icon: <TechTalksIcom />,
      slug: 'tech-talks',
      activeStyles,
      visible: company.socialPosts.count > 0
    },
    {
      name: 'Members',
      icon: <MembersIcon />,
      slug: 'members',
      activeStyles,
      visible: isPrivateMode && !isPrivateMode.loading && company.privateMode && isPrivate
    },
    {
      name: 'Members',
      icon: <MembersIcon />,
      slug: 'members',
      activeStyles,
      visible: company.members.count > 0 && !company.privateMode && !isPrivate
    },
    {
      name: 'Followers',
      icon: <FollowersIcon />,
      slug: 'followers',
      activeStyles,
      visible: isPrivate && company.followers.count > 0
    }
  ];

  const visibleMenuItems = allMenuOptions
    .filter(item => {
      return item.visible === true;
    })
    .map(item => {
      return item;
    });
  return (
    <Container>
      <NavigationBar
        items={visibleMenuItems}
        ref={stickyRef}
        itemWidth={85}
        onItemClick={item => sendAnalyticsEvent('click_navbar', {name: item.name})}
      >
        {children}
      </NavigationBar>
    </Container>
  );
};

Navigation.propTypes = {
  company: PropTypes.object,
  children: PropTypes.any
};

export default Navigation;
